import type { ReactNode } from 'react';
import { Svg } from '../../primitives/Svg.js';
import { useDecorativeIconMotion } from '../../primitives/useIconMotion.js';
import type { IconProps } from '../../primitives/types.js';

interface Twinkle {
  readonly cx: number;
  readonly cy: number;
  readonly r: number;
  readonly delay: number;
}

const TWINKLES: readonly Twinkle[] = [
  { cx: 12.5, cy: 12, r: 0.8, delay: 0 },
  { cx: 3.5, cy: 13, r: 0.6, delay: 0.3 },
  { cx: 13, cy: 3.5, r: 0.55, delay: 0.75 },
  { cx: 3, cy: 3.5, r: 0.4, delay: 1.15 },
];

/** Sparkle — main glyph breathes in scale + rotates slightly while
 *  satellites twinkle in staggered bursts (grow → fade). Evokes an
 *  "AI magic" or "featured" flourish. */
export function IcnSparkle(props: IconProps): ReactNode {
  const animate = useDecorativeIconMotion();
  return (
    <Svg {...props} iconName="sparkle">
      <g style={{ transformOrigin: '8px 6px' }}>
        {animate && (
          <animateTransform
            attributeName="transform"
            type="rotate"
            values="0 8 6;10 8 6;-10 8 6;0 8 6"
            dur="2.4s"
            repeatCount="indefinite"
          />
        )}
        <path
          d="M8 2l1 3.5L12.5 6 9 7l-1 3.5L7 7 3.5 6 7 5.5 8 2z"
          fill="currentColor"
          stroke="none"
        >
          {animate && (
            <animate
              attributeName="opacity"
              values="0.85;1;0.85"
              dur="1.8s"
              repeatCount="indefinite"
            />
          )}
        </path>
      </g>
      {animate && (
        <g stroke="none" fill="currentColor">
          {TWINKLES.map((t) => {
            const key = `${String(t.cx)}-${String(t.cy)}`;
            const begin = `${String(t.delay)}s`;
            const rValues = `0;${String(t.r)};0`;
            return (
              <circle key={key} cx={t.cx} cy={t.cy} r={t.r} opacity="0">
                <animate
                  attributeName="opacity"
                  values="0;1;0"
                  keyTimes="0;0.3;1"
                  dur="1.8s"
                  begin={begin}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="r"
                  values={rValues}
                  keyTimes="0;0.3;1"
                  dur="1.8s"
                  begin={begin}
                  repeatCount="indefinite"
                />
              </circle>
            );
          })}
        </g>
      )}
    </Svg>
  );
}
